import { config } from "roo-shared";

/**
 * Provider sign-in, reduced to one job: **put a provider ID token in our hands.**
 *
 * Everything the browser side of "Continue with Google" needs — lazily fetching the provider's
 * script, initialising it with our client id, and rendering the provider's own branded button —
 * lives behind this module. Pages stay declarative and `session.service` keeps owning tokens and
 * session state, exactly as it does for the email-code path.
 *
 * ## Why Google gets a MOUNT rather than the `getGoogleIdToken()` promise the plan sketched
 *
 * Google Identity Services does not expose "open the sign-in popup" as a callable. The supported
 * ID-token flow is `renderButton`, which draws Google's own button (whose branding we're required
 * to use anyway — see the README's roo-theme note) and invokes a callback on **every** successful
 * sign-in. A one-shot promise would have to be re-armed after each press, and could never resolve
 * for the cancel case: GIS reports nothing at all when the user closes the popup, so a promise
 * would simply hang forever.
 *
 * Apple's `AppleID.auth.signIn()` genuinely is one-shot — it resolves with the token and rejects
 * with `popup_closed_by_user` — so Phase 4 adds `getAppleIdToken()` in the promise shape. The two
 * differ because the providers differ, not by accident.
 */

const GOOGLE_SCRIPT_URL = "https://accounts.google.com/gsi/client";
const APPLE_SCRIPT_URL = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

/**
 * A provider script that never loads must fail rather than hang. `onerror` covers a refused or
 * blocked request, but not a connection that is accepted and then stalls — the same reasoning as
 * the SDK's request timeout.
 */
const SCRIPT_LOAD_TIMEOUT_MS = 10_000;

type GoogleCredentialResponse = { credential?: string };

type GoogleIdApi = {
  initialize: (p: {
    client_id: string;
    callback: (response: GoogleCredentialResponse) => void;
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
  }) => void;
  renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void;
  cancel: () => void;
};

type AppleSignInResponse = {
  authorization?: { id_token?: string };
  /** Present on the FIRST authorization only — see `AppleCredential.firstName`. */
  user?: { name?: { firstName?: string; lastName?: string } };
};

type AppleAuthApi = {
  init: (p: { clientId: string; scope: string; redirectURI: string; usePopup: boolean }) => void;
  signIn: () => Promise<AppleSignInResponse>;
};

declare global {
  interface Window {
    google?: { accounts?: { id?: GoogleIdApi } };
    AppleID?: { auth?: AppleAuthApi };
  }
}

/** Thrown when a provider can't be offered at all — the caller shows a message and keeps email. */
export class SocialAuthUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SocialAuthUnavailableError";
  }
}

/**
 * One in-flight load per script URL, so two buttons on one page don't inject two `<script>` tags.
 * A FAILED load is deliberately forgotten (see below) — otherwise the first network blip would
 * poison the provider for the rest of the page's life, with a reload as the only way back.
 */
const scriptLoads = new Map<string, Promise<void>>();

function loadProviderScript(url: string): Promise<void> {
  const inFlight = scriptLoads.get(url);
  if (inFlight) return inFlight;

  let failedScript: HTMLScriptElement | null = null;

  const load = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = url;
    script.async = true;
    const fail = (error: SocialAuthUnavailableError) => {
      // Remembered so the catch below can remove it. Leaving a dead tag in `<head>` means every
      // retry stacks another one, and a timed-out tag may still be in flight — if it later loads it
      // would populate the provider global behind a UI that has already given up on it.
      failedScript = script;
      reject(error);
    };
    const timer = window.setTimeout(
      () => fail(new SocialAuthUnavailableError(`Timed out loading ${url}`)),
      SCRIPT_LOAD_TIMEOUT_MS,
    );
    script.onload = () => {
      window.clearTimeout(timer);
      resolve();
    };
    script.onerror = () => {
      window.clearTimeout(timer);
      fail(new SocialAuthUnavailableError(`Failed to load ${url}`));
    };
    document.head.appendChild(script);
  }).catch((e: unknown) => {
    scriptLoads.delete(url);
    failedScript?.remove();
    throw e;
  });

  scriptLoads.set(url, load);
  return load;
}

/**
 * Whether "Continue with Google" can be offered at all. Empty client id ⇒ the provider is not
 * configured for this environment and the button must not render — a rendered button that can only
 * fail is worse than no button.
 */
export function isGoogleConfigured(): boolean {
  return config.GOOGLE_AUTH.webClientId.length > 0;
}

/**
 * Whether "Continue with Apple" can be offered. Blank until the Services ID exists in the Apple
 * Developer portal and the app is served from a domain-verified HTTPS origin — see
 * `AppleAuthConfig.webServicesId`. Phase 4 renders the button off this; nothing else does yet.
 */
export function isAppleConfigured(): boolean {
  return config.APPLE_AUTH.webServicesId.length > 0;
}

/**
 * Render Google's sign-in button into `container` and hand every resulting credential to
 * `onIdToken`.
 *
 * Rejects with `SocialAuthUnavailableError` when the provider can't be offered (not configured,
 * script unreachable). It does NOT reject when the user cancels — GIS gives us no such signal, and
 * a cancel is not an error condition anyway.
 */
export async function mountGoogleButton(p: {
  container: HTMLElement;
  onIdToken: (idToken: string) => void;
}): Promise<void> {
  if (!isGoogleConfigured()) {
    throw new SocialAuthUnavailableError("Google sign-in is not configured for this environment");
  }

  await loadProviderScript(GOOGLE_SCRIPT_URL);

  const api = window.google?.accounts?.id;
  if (!api) {
    throw new SocialAuthUnavailableError("Google sign-in loaded but exposed no API");
  }

  api.initialize({
    client_id: config.GOOGLE_AUTH.webClientId,
    callback: (response) => {
      // A response with no credential is a shape we don't understand; treating it as a cancel is
      // the safe reading — the alternative is calling the server with `undefined`.
      if (response.credential) p.onIdToken(response.credential);
    },
    // Both off deliberately: `auto_select` would sign a returning user in with no gesture at all,
    // which is not something a login page should do on load.
    auto_select: false,
    cancel_on_tap_outside: true,
  });

  // Clear first so a re-mount (React effect re-run, a retry after a failure) replaces the button
  // rather than stacking a second one.
  p.container.replaceChildren();
  api.renderButton(p.container, {
    type: "standard",
    theme: "outline",
    size: "large",
    text: "continue_with",
    shape: "rectangular",
    logo_alignment: "center",
    width: 320,
  });
}

/** Tear down a mounted button. Safe to call when nothing was ever mounted. */
export function unmountGoogleButton(container: HTMLElement): void {
  container.replaceChildren();
  window.google?.accounts?.id?.cancel();
}

/**
 * What Apple hands back on a successful sign-in.
 *
 * The name rides along with the token because **Apple sends it exactly once**, on the very first
 * authorization, and never again — not in the ID token, and not on any later sign-in. If we drop it
 * here the account is created nameless and nothing can recover it. That is why `signUpWithApple`
 * takes optional name fields while `signUpWithGoogle` takes none: Google puts `given_name` /
 * `family_name` in the verified claims, so the server reads them itself.
 */
export type AppleCredential = {
  identityToken: string;
  firstName?: string;
  lastName?: string;
};

/**
 * The name Apple released on the FIRST authorization in this page session, kept so a later call can
 * still produce it.
 *
 * Apple sends `user.name` exactly once per app+user, ever — not on any subsequent `signIn()`, and
 * never in the ID token. That collides with our own login→signup bounce: a NEW user's first
 * authorization happens on the LOGIN page (which needs only the token), the server answers
 * `UNABLE_TO_FIND_USER`, we route them to SignUp, and their second press is a *re*-authorization
 * that Apple answers without a name — so the account was created with `first_name: ""` and nothing
 * could ever recover it.
 *
 * Remembering it HERE rather than threading it through the page tree keeps the quirk owned by the
 * module that owns the handshake: neither page has to know Apple behaves this way, and any future
 * caller inherits the fix. Scope is the page session, which is exactly the window the bounce lives in.
 *
 * Staleness is not a risk: the only way to get a nameless response is to have authorized before,
 * which means the account already exists, which means `signUpWithApple` takes its `success_login`
 * exit and ignores the name fields entirely.
 */
let lastReleasedAppleName: { firstName?: string; lastName?: string } | null = null;

/** Apple's own words for "the user closed the sheet" — neither is an error worth showing. */
const APPLE_CANCEL_CODES = new Set([
  "popup_closed_by_user",
  "user_cancelled_authorize",
  "user_trigger_new_signin_flow",
]);

function isAppleCancellation(error: unknown): boolean {
  const code = (error as { error?: unknown } | null)?.error;
  return typeof code === "string" && APPLE_CANCEL_CODES.has(code);
}

/**
 * Open Apple's sign-in sheet and resolve with the credential — or **`null` when the user cancels**,
 * which is a normal outcome and not an error.
 *
 * Unlike Google this genuinely is one-shot (`AppleID.auth.signIn()` resolves with the token and
 * rejects on dismissal), so the promise shape D4 sketched fits it exactly. The caller therefore owns
 * the button, which is why the Apple button is hand-rolled to Apple's guidelines rather than drawn
 * by the provider: Apple's own rendered button reports through `document` events instead, and mixing
 * the two models would mean two ways for one credential to arrive.
 *
 * Throws `SocialAuthUnavailableError` when Apple can't be offered at all.
 */
export async function getAppleIdToken(): Promise<AppleCredential | null> {
  if (!isAppleConfigured()) {
    throw new SocialAuthUnavailableError("Apple sign-in is not configured for this environment");
  }

  await loadProviderScript(APPLE_SCRIPT_URL);

  const api = window.AppleID?.auth;
  if (!api) {
    throw new SocialAuthUnavailableError("Apple sign-in loaded but exposed no API");
  }

  api.init({
    clientId: config.APPLE_AUTH.webServicesId,
    // `name` is what makes Apple include the one-time name above; `email` is what the server keys
    // the account on.
    scope: "name email",
    // Apple requires a registered, domain-verified HTTPS return URL and permits neither `localhost`
    // nor plain HTTP — which is exactly why this cannot be exercised on a devpod, and why the
    // Services ID stays blank until `app.roo.money` is attached. `WEB_APP_URL` is the same value the
    // Pages custom domain must equal, so the two can't drift apart.
    redirectURI: config.WEB_APP_URL,
    // With a popup the token comes back to THIS page. The redirect flow would navigate away and
    // post the credential to `redirectURI` as a form POST, which a static site cannot receive.
    usePopup: true,
  });

  let response: AppleSignInResponse;
  try {
    response = await api.signIn();
  } catch (error) {
    if (isAppleCancellation(error)) return null;
    throw error;
  }

  const identityToken = response.authorization?.id_token;
  if (!identityToken) {
    // A resolved sign-in with no token is a shape we don't understand. Reading it as a cancel would
    // silently do nothing; this at least tells the user something went wrong.
    throw new SocialAuthUnavailableError("Apple sign-in returned no identity token");
  }

  const releasedName = response.user?.name;
  if (releasedName?.firstName || releasedName?.lastName) {
    lastReleasedAppleName = { firstName: releasedName.firstName, lastName: releasedName.lastName };
  }

  // Fall back to the remembered name, so a second authorization in the same page session (the
  // login→signup bounce) still carries what Apple only ever said once. The TOKEN is always the
  // fresh one — only the name is restored.
  return {
    identityToken,
    firstName: releasedName?.firstName ?? lastReleasedAppleName?.firstName,
    lastName: releasedName?.lastName ?? lastReleasedAppleName?.lastName,
  };
}

/** Test seam — resets the one-time-name memory so a spec can model a first vs repeat authorization. */
export function __resetAppleNameMemoryForTests(): void {
  lastReleasedAppleName = null;
}
